import { FC, useState, useRef, useEffect, useCallback } from 'react';
import { InputText } from 'primereact/inputtext';
import { OverlayPanel } from 'primereact/overlaypanel';
import { FieldMetadata } from '../../models/expression';
import styles from './TokenInput.module.css';

interface TokenInputProps {
  value: string | null;
  onChange: (value: string | null) => void;
  tokenFields: FieldMetadata[];
  placeholder?: string;
}

export const TokenInput: FC<TokenInputProps> = ({ value, onChange, tokenFields, placeholder }) => {
  const [filterText, setFilterText] = useState('');
  const [showOverlay, setShowOverlay] = useState(false);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [tokenStartPosition, setTokenStartPosition] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const overlayRef = useRef<OverlayPanel>(null);

  const filteredFields = tokenFields.filter((field) =>
    field.fieldName?.toLowerCase().includes(filterText.toLowerCase())
  );

  const handleCursorChange = () => {
    const cursorPos = inputRef.current?.selectionStart ?? 0;
    setCursorPosition(cursorPos);
  };

  const handleInputChange = (inputValue: string) => {
    onChange(inputValue || null);

    const cursorPos = inputRef.current?.selectionStart ?? inputValue.length;
    setCursorPosition(cursorPos);

    const textBeforeCursor = inputValue.substring(0, cursorPos);
    const lastOpenBraces = textBeforeCursor.lastIndexOf('{{');

    if (lastOpenBraces !== -1) {
      const textAfterBraces = textBeforeCursor.substring(lastOpenBraces + 2);
      const hasClosingBraces = textAfterBraces.includes('}}');

      if (!hasClosingBraces) {
        setTokenStartPosition(lastOpenBraces);
        setFilterText(textAfterBraces.trim());
        if (!showOverlay) {
          setShowOverlay(true);
          overlayRef.current?.show(null, inputRef.current);
        }
        return;
      }
    }

    if (showOverlay) {
      setShowOverlay(false);
      overlayRef.current?.hide();
      setTokenStartPosition(null);
      setFilterText('');
    }
  };

  const handleSelectField = useCallback((fieldName: string) => {
    if (tokenStartPosition === null) return;

    const currentValue = value ?? '';
    const beforeToken = currentValue.substring(0, tokenStartPosition);
    const afterCursor = currentValue.substring(cursorPosition);
    const newValue = `${beforeToken}{{${fieldName}}}${afterCursor}`;

    onChange(newValue);
    setShowOverlay(false);
    overlayRef.current?.hide();
    setTokenStartPosition(null);
    setFilterText('');

    setTimeout(() => {
      const newCursorPos = beforeToken.length + fieldName.length + 4;
      inputRef.current?.setSelectionRange(newCursorPos, newCursorPos);
      inputRef.current?.focus();
    }, 0);
  }, [tokenStartPosition, cursorPosition, value, onChange]);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (showOverlay && event.key === 'Escape') {
      setShowOverlay(false);
      overlayRef.current?.hide();
      setTokenStartPosition(null);
      setFilterText('');
      event.preventDefault();
      event.stopPropagation();
    }
  };

  useEffect(() => {
    const handleClickOutside = () => {
      if (showOverlay) {
        setShowOverlay(false);
        overlayRef.current?.hide();
        setTokenStartPosition(null);
        setFilterText('');
      }
    };

    if (showOverlay) {
      const timer = setTimeout(() => {
        document.addEventListener('click', handleClickOutside, { once: true });
      }, 100);
      return () => {
        clearTimeout(timer);
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [showOverlay]);

  return (
    <div className={styles.tokenInputWrapper}>
      <InputText
        ref={inputRef}
        value={value ?? ''}
        onChange={(e) => handleInputChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onSelect={handleCursorChange}
        placeholder={placeholder ?? 'Enter value (use {{ for tokens)'}
        className="w-full"
      />
      <OverlayPanel
        ref={overlayRef}
        dismissable={false}
        style={{ maxHeight: '250px', minWidth: '300px', padding: 0 }}
      >
        <div className={styles.tokenList}>
          {filteredFields.length === 0 ? (
            <div className={styles.noResults}>No matching fields</div>
          ) : (
            filteredFields.slice(0, 50).map((field) => (
              <div
                key={field.fieldName}
                className={styles.tokenItem}
                onClick={() => field.fieldName && handleSelectField(field.fieldName)}
              >
                <span className={styles.tokenFieldName}>{field.fieldName}</span>
                <span className={styles.tokenDataType}>{field.dataType}</span>
              </div>
            ))
          )}
        </div>
      </OverlayPanel>
    </div>
  );
};
